import {isNotNil} from 'ramda';

import {PublicFinancialProductsResponseBody} from '../../api/types/onlineOfferTypes';

/**
 * How an answer about the offer was come by. Three answers describe what the
 * offer holds — what was polled, what a save was acknowledged with, and what a
 * refused save reported — and they only differ in standing when they describe
 * the same revision.
 */
export type FinancialProductsAuthority = 'POLLED' | 'ACKNOWLEDGED' | 'REFUSED';

export interface AuthoritativeFinancialProducts {
  body: PublicFinancialProductsResponseBody | undefined;
  authority: FinancialProductsAuthority;
}

interface AnsweredFinancialProducts {
  body: PublicFinancialProductsResponseBody;
  authority: FinancialProductsAuthority;
}

/**
 * Who to believe about one and the same revision. A write the offer answered
 * read the preference while holding it; a poll only observed it from outside.
 */
const STANDING: Record<FinancialProductsAuthority, number> = {
  POLLED: 0,
  ACKNOWLEDGED: 1,
  REFUSED: 2,
};

const byAuthority = (left: AnsweredFinancialProducts, right: AnsweredFinancialProducts) =>
  left.body.preferenceRevision === right.body.preferenceRevision
    ? STANDING[left.authority] - STANDING[right.authority]
    : left.body.preferenceRevision - right.body.preferenceRevision;

/**
 * Which of several answers about the same offer states what it now holds.
 *
 * The revision decides, never the moment the answer landed: a response is not
 * newer data for having taken longer to arrive, and a save refused for a
 * revision conflict answers with a state that a slower answer about the
 * revision it superseded must not put back on the screen.
 */
export const pickAuthoritativeFinancialProducts = (
  answers: AuthoritativeFinancialProducts[]
): PublicFinancialProductsResponseBody | undefined =>
  answers
    .flatMap((answer) =>
      isNotNil(answer.body) ? [{body: answer.body, authority: answer.authority}] : []
    )
    .sort(byAuthority)
    .at(-1)?.body;
